import {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Camera,
  ImageUp,
} from "lucide-react";

import {
  CameraScanner,
} from "./CameraScanner";
import {
  createMrzScanner,
  type MrzScanner,
} from "./mrzScanner";

type ScanState =
  | { kind: "idle" }
  | { kind: "camera" }
  | { kind: "working" }
  | { kind: "done"; applied: number }
  | { kind: "error"; message: string };

// One OCR worker pair for the whole page, shared by every section that scans
// (a family's form has one per traveller). The camera loop reuses it across
// frames, and booting it (WASM + language models) costs seconds and tens of
// MB, so it is reference-counted: the first scan boots it, the last section to
// unmount disposes it. A failed boot clears the slot so the next attempt
// actually retries instead of replaying the cached rejection until reload.
let sharedScanner: Promise<MrzScanner> | null = null;
let sharedScannerHolders = 0;

function acquireScanner(): Promise<MrzScanner> {
  sharedScanner ??= createMrzScanner().catch((err: unknown) => {
    sharedScanner = null;
    throw err;
  });
  return sharedScanner;
}

function releaseScanner(): void {
  sharedScannerHolders -= 1;
  if (sharedScannerHolders > 0 || sharedScanner === null) return;
  const scanner = sharedScanner;
  sharedScanner = null;
  // The trailing catch covers releasing while a boot is still pending that
  // then fails — nothing to dispose, and nobody left to care.
  void scanner.then((s) => s.dispose()).catch(() => undefined);
}

const CANT_READ_MESSAGE =
  "Couldn't read the passport. Try a sharper, well-lit photo of the photo page — or just type the details below.";

/**
 * "Scan your passport" affordance for the vault travel-details form. Reads
 * the MRZ (the two machine-readable lines at the bottom of the photo page)
 * with on-device OCR — live from the camera where available, or from an
 * uploaded photo — and prefills whatever validates against its check digits.
 * The user reviews before saving, and nothing is ever uploaded.
 */
export function PassportScanSection({
  onApply,
}: {
  onApply: (values: Record<string, string>) => number;
}) {
  const [state, setState] = useState<ScanState>({ kind: "idle" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const holdsScanner = useRef(false);

  const getScanner = (): Promise<MrzScanner> => {
    if (!holdsScanner.current) {
      holdsScanner.current = true;
      sharedScannerHolders += 1;
    }
    return acquireScanner();
  };
  useEffect(
    () => () => {
      if (holdsScanner.current) releaseScanner();
    },
    [],
  );

  const applyScan = (values: Record<string, string>) => {
    const applied = onApply(values);
    if (applied === 0) {
      setState({ kind: "error", message: CANT_READ_MESSAGE });
      return;
    }
    setState({ kind: "done", applied });
  };

  const handleFile = async (file: File) => {
    setState({ kind: "working" });
    try {
      const values = await (await getScanner()).scan(file);
      if (values === null) {
        setState({ kind: "error", message: CANT_READ_MESSAGE });
        return;
      }
      applyScan(values);
    } catch (err) {
      // The user-facing message stays generic; the cause is for the console.
      console.error("Passport scan failed", err);
      setState({ kind: "error", message: CANT_READ_MESSAGE });
    }
  };

  const cameraSupported =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);
  const busy = state.kind === "working" || state.kind === "camera";

  return (
    <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Allow re-picking the same photo after a failed attempt.
          e.target.value = "";
          if (file) void handleFile(file);
        }}
      />

      {state.kind === "camera" ? (
        <CameraScanner
          scanner={getScanner}
          onFound={applyScan}
          onUnavailable={(message) => setState({ kind: "error", message })}
          onCancel={() => setState({ kind: "idle" })}
        />
      ) : (
        <>
          <div className="flex flex-col gap-2 sm:flex-row">
            {cameraSupported && (
              <button
                type="button"
                disabled={busy}
                onClick={() => setState({ kind: "camera" })}
                className="flex flex-1 items-center justify-center gap-2 rounded-md border border-catch-red bg-white px-4 py-2 text-sm font-medium text-catch-red hover:bg-catch-red/10 disabled:opacity-50"
              >
                <Camera className="h-5 w-5" aria-hidden="true" />
                Scan your passport live
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 ${
                cameraSupported
                  ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  : "border border-catch-red bg-white text-catch-red hover:bg-catch-red/10"
              }`}
            >
              <ImageUp className="h-5 w-5" aria-hidden="true" />
              {state.kind === "working"
                ? "Reading your passport…"
                : "Upload a photo"}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Point the camera at the photo page — it's read right here on your
            device and never uploaded.
          </p>
        </>
      )}

      {state.kind === "done" && (
        <p className="mt-2 text-xs text-green-700" role="status">
          Filled {state.applied} {state.applied === 1 ? "field" : "fields"}{" "}
          from your passport — double-check them before saving.
        </p>
      )}
      {state.kind === "error" && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          {state.message}
        </p>
      )}
    </div>
  );
}
